import { api_fetch, api_fetch_throws, DeductionDetailJson, User } from "@/api"
import { toast } from "@/components/ui/use-toast"
import { playbook_action_hint } from "@/deduction/dispute_playbook.ts"
import { getSecondaryActions, ButtonAction, SecondaryActionsModel } from "@/deduction_detail/detail_action_model"
import { useFetch } from "@/utils"
import { DrawerDeduction } from "@/dispute/file_dispute_drawer.tsx"
import { ChevronDown, CircleCheck, DoorOpen, Info, ShieldAlert, Sparkles, Wand2 } from "lucide-react"
import { Button } from "@/components/ui/button.tsx"
import useLocation, { useSearch } from "wouter-preact/use-location"
import { useState } from "preact/compat"
import { assign_tasks, complete_task, TaskStatus, TaskSubmission } from "@/api/task.tsx"
import { formatTaskType } from "@/deduction_detail/Timeline2"
import { PendingTaskConfirmDialog } from "@/deduction_table/pending_task_confirm_dialog.tsx"
import { toastResult } from "@/toast.tsx"
import { RefreshFlag } from "@/refresh_flag.tsx"
import { AssignmentForm } from "@/deduction_table/assignment_form.tsx"
import { closeModalAndNavigate } from "@/deduction/deduction_detail_modal_state"
import { requestBackup, useBackupRequestInFlight } from "@/deduction_next_action"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { AssignRepaymentDialog } from "@/deduction_detail/AssignRepaymentDialog"
import { UserState } from "@/auth/user"
import { run_big_wiz } from "@/deduction/run_big_wiz"
import { ShortPayDisputeDialog } from "@/accounting/ShortPayDisputeDialog.tsx"
import { RemoveDuplicateDialog } from "@/deduction_detail/RemoveDuplicateDialog"

function buttonIcon(action: ButtonAction) {
  switch (action.type) {
    case "wizard":
      return <Wand2 className="h-4 w-4 mr-2" />
    case "big_wiz":
      return <Sparkles className="h-4 w-4 mr-2" />
    case "assign":
      return <CircleCheck className="h-4 w-4 mr-2" />
    case "file_dispute":
    case "dispute_short_pay":
      return <ShieldAlert className="w-4 mr-1" />
    case "request_exit_pass":
      return <DoorOpen className="h-4 w-4 mr-2" />
    default:
      return null
  }
}

function primaryVariant(action: ButtonAction): "default" | "outline" | "tertiary" {
  if (action.type === "file_dispute" && action.url) return "tertiary"
  if (action.type === "file_dispute") return "default"
  return "outline"
}

interface SplitButtonProps {
  model: SecondaryActionsModel
  deduction: DeductionDetailJson
  navigate: (url: string) => void
  onTriggerWizard: () => void
}

function SecondaryActionsSplitButton({ model, deduction, navigate, onTriggerWizard }: SplitButtonProps) {
  const [repaymentDialogOpen, setRepaymentDialogOpen] = useState(false)
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false)
  const [shortPayDialogOpen, setShortPayDialogOpen] = useState(false)
  const [removeDuplicateOpen, setRemoveDuplicateOpen] = useState(false)
  const [bigWizRunning, setBigWizRunning] = useState(false)
  const [exitPassRequesting, setExitPassRequesting] = useState(false)
  const backupRequestInFlight = useBackupRequestInFlight(deduction.id)
  const backupRequestDisabled = backupRequestInFlight || deduction.has_backup
  const { data: users } = useFetch(() => api_fetch_throws<User[]>("/users"), [], [])

  // Tasks already pending on this deduction when the assignment dialog was opened; assigning on
  // top of them prompts whether they should be marked complete first.
  const pending_tasks = (deduction.tasks ?? []).filter(t => t.status === TaskStatus.Pending)
  const [pendingConfirmValues, setPendingConfirmValues] = useState<any | null>(null)
  const [assignBusy, setAssignBusy] = useState(false)

  async function submitAssignment(values: any, complete_existing: boolean) {
    setAssignBusy(true)
    try {
      if (complete_existing) {
        // Don't assign on top of tasks we failed to close — the user chose "mark existing
        // complete", so reporting success while they are still pending would be a lie.
        const results = await Promise.all(pending_tasks.map(t => complete_task(t.id)))
        const failed = results.filter(r => !r.ok)
        if (failed.length > 0) {
          toastResult(failed[0], "", "Failed to complete the existing task")
          return
        }
      }
      const submissions: TaskSubmission[] = values.user_ids.map((user_id: string) => ({
        deduction_id: deduction.id,
        user_id,
        task_type: values.taskType,
        backup_type: values.backupType,
        note: values.note,
        is_time_sensitive: values.isTimeSensitive,
      }))

      let res = await assign_tasks(submissions)
      toastResult(
        res,
        `${submissions.length > 1 ? `${submissions.length} tasks` : "Task"} assigned successfully`,
        "Failed to assign task"
      )
      if (res.ok) {
        setPendingConfirmValues(null)
        setAssignmentDialogOpen(false)
        RefreshFlag.set(flag => !flag)
      }
    } finally {
      setAssignBusy(false)
    }
  }

  async function handleTaskAssignment(values: any) {
    if (pending_tasks.length > 0) {
      setPendingConfirmValues(values)
      return
    }
    await submitAssignment(values, false)
  }

  async function handleBigWiz() {
    if (bigWizRunning) return
    setBigWizRunning(true)
    try {
      await run_big_wiz([deduction.id])
    } finally {
      setBigWizRunning(false)
    }
  }

  // The exit pass goes to the org's exit-pass contact (Settings → Logistics Providers). The server
  // says so when none is set up; the toast carries its message.
  async function handleRequestExitPass() {
    if (exitPassRequesting) return
    setExitPassRequesting(true)
    try {
      const res = await api_fetch<{ message: string }>(`/deduction/${deduction.id}/request-exit-pass`, { method: "POST", body: {} })
      if (res.ok) {
        toast({ title: "Exit pass requested", description: res.value.data.message })
        RefreshFlag.set(flag => !flag)
      } else {
        toast({ title: "Could not request the exit pass", description: res.error?.message, variant: "destructive" })
      }
    } finally {
      setExitPassRequesting(false)
    }
  }

  // Label / disabled / tooltip are shared by the primary button and the dropdown
  // items so an in-flight action reads the same wherever it is rendered.
  function actionLabel(action: ButtonAction): string {
    if (action.type === "request_backup" && backupRequestInFlight) return "Requesting…"
    if (action.type === "big_wiz" && bigWizRunning) return "Running…"
    if (action.type === "request_exit_pass" && exitPassRequesting) return "Requesting…"
    return action.label
  }

  function actionDisabled(action: ButtonAction): boolean {
    if (action.type === "request_backup") return backupRequestDisabled
    if (action.type === "big_wiz") return bigWizRunning
    if (action.type === "request_exit_pass") return exitPassRequesting
    return false
  }

  function actionTitle(action: ButtonAction): string | undefined {
    if (action.type === "request_exit_pass") return "Email the warehouse's exit-pass contact for this order's gate pass"
    if (action.type === "request_backup" && deduction.has_backup) return "Backup already synced from portal"
    if (action.type === "request_backup" || action.type === "file_dispute") {
      return playbook_action_hint(deduction.dispute_playbook, action.type, deduction.category_dispute_email_to)
    }
    return undefined
  }

  function handleAction(action: ButtonAction) {
    switch (action.type) {
      case "wizard":
        onTriggerWizard()
        break
      case "big_wiz":
        void handleBigWiz()
        break
      case "assign":
        setAssignmentDialogOpen(true)
        break
      case "request_backup":
        if (!backupRequestDisabled) {
          requestBackup(deduction)
        }
        break
      case "file_dispute":
        if (action.url) {
          window.open(action.url)
        } else {
          DrawerDeduction.set(deduction)
        }
        break
      case "dispute_short_pay":
        setShortPayDialogOpen(true)
        break
      case "remove_duplicate":
        setRemoveDuplicateOpen(true)
        break
      case "request_exit_pass":
        void handleRequestExitPass()
        break
      case "view_deduction":
        closeModalAndNavigate(navigate, `/deduction/${action.deductionId}`)
        break
      case "assign_repayment":
      case "change_repayment":
        setRepaymentDialogOpen(true)
        break
    }
  }

  let [primary, ...secondary] = model.buttons

  return (
    <>
      {model.status && (
        <span
          className={`flex items-center ${model.status.type === "disputed" ? "text-green-600" : "text-red-500"}`}
          onClick={() => {
            if (model.status!.clickable) {
              DrawerDeduction.set(deduction)
            }
          }}
          style={model.status.clickable ? { cursor: "pointer" } : undefined}>
          <ShieldAlert className="w-4 mr-1" />
          {model.status.type === "disputed" ? model.status.label : model.status.message}
        </span>
      )}
      {deduction.backup_unavailable_reason && !deduction.has_backup && (
        <span
          className="flex items-start text-muted-foreground max-w-md"
          title={deduction.backup_unavailable_reason}>
          <Info className="w-4 h-4 mr-1 mt-0.5 shrink-0" />
          <span className="text-xs leading-snug">
            {deduction.backup_unavailable_reason}
            {deduction.backup_portal && (
              <>
                {" "}
                <a
                  href={deduction.backup_portal.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-700 hover:underline whitespace-nowrap">
                  Open the {deduction.backup_portal.name} ↗
                </a>
              </>
            )}
          </span>
        </span>
      )}
      {primary && (
        <div className="flex">
          <Button
            variant={primaryVariant(primary)}
            className={secondary.length > 0 ? "rounded-r-none border-r-0" : ""}
            disabled={actionDisabled(primary)}
            title={actionTitle(primary)}
            onClick={() => handleAction(primary)}>
            {buttonIcon(primary)}
            {actionLabel(primary)}
          </Button>
          {secondary.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant={primaryVariant(primary)} className="rounded-l-none px-2">
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {secondary.map(action => (
                  <DropdownMenuItem
                    key={action.type}
                    disabled={actionDisabled(action)}
                    title={actionTitle(action)}
                    onClick={() => handleAction(action)}>
                    {buttonIcon(action)}
                    {actionLabel(action)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
      {/* Assignment Dialog */}
      <Dialog open={assignmentDialogOpen} onOpenChange={setAssignmentDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Assign Task</DialogTitle>
          </DialogHeader>
          <AssignmentForm users={users} onSubmit={handleTaskAssignment} />
        </DialogContent>
      </Dialog>
      {/* Existing pending task confirmation */}
      <PendingTaskConfirmDialog
        open={pendingConfirmValues !== null}
        busy={assignBusy}
        onCompleteExisting={() => void submitAssignment(pendingConfirmValues, true)}
        onKeepExisting={() => void submitAssignment(pendingConfirmValues, false)}
        onCancel={() => setPendingConfirmValues(null)}>
        <div>
          This deduction already has {pending_tasks.length === 1 ? "a pending task" : `${pending_tasks.length} pending tasks`}:
        </div>
        <ul className="list-disc pl-5">
          {pending_tasks.map(task => (
            <li key={task.id}>
              {formatTaskType(task.task_type)} — {task.user_display_name}
            </li>
          ))}
        </ul>
        <div>Mark {pending_tasks.length === 1 ? "it" : "them"} complete before assigning the new task?</div>
      </PendingTaskConfirmDialog>
      {/* Repayment Assignment Dialog */}
      <AssignRepaymentDialog deduction={deduction} open={repaymentDialogOpen} onOpenChange={setRepaymentDialogOpen} />
      {/* Short pay claim against this invoice payment row */}
      <ShortPayDisputeDialog
        target={shortPayDialogOpen ? { kind: "deduction", deduction } : null}
        onClose={() => setShortPayDialogOpen(false)}
        onFiled={() => RefreshFlag.set(flag => !flag)}
      />
      {/* Merge this row into its other copy */}
      <RemoveDuplicateDialog
        deduction={deduction}
        open={removeDuplicateOpen}
        onOpenChange={setRemoveDuplicateOpen}
        onRemoved={(kept_id: string) => closeModalAndNavigate(navigate, `/deduction/${kept_id}`)}
      />
    </>
  )
}

interface DetailActionsProps {
  deduction: DeductionDetailJson
  onTriggerWizard: () => void
}

export function DeductionDetailActions({ deduction, onTriggerWizard }: DetailActionsProps) {
  const [, setLocation] = useLocation()
  const user = UserState.use()
  const canRunBigWiz = Boolean(user)

  let model = getSecondaryActions(deduction, { canRunBigWiz })

  return (
    <div id="secondary-actions" class="flex items-center space-x-2">
      <SecondaryActionsSplitButton
        model={model}
        deduction={deduction}
        navigate={setLocation}
        onTriggerWizard={onTriggerWizard}
      />
    </div>
  )
}
